
import React, { useState, useRef, useEffect } from "react";
import {
  Download,
  FileText,
  Upload,
  CheckCircle,
  X,
  Loader,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ClientSelector from "../ClientSelector";

function TargetsAndQualifications() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [progressMessage, setProgressMessage] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadFilename, setDownloadFilename] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");

  const fileInputRef = useRef(null);
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        window.URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 20000);
  };

  const handleFileSelect = (selectedFile) => {
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      if (downloadUrl) {
        window.URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
        setDownloadFilename("");
      }
      setFile(selectedFile);
      setUploadResult(null); // Clear previous results
      showMessage("File selected successfully", "success");
    } else {
      showMessage("Please select a valid Excel file (.xlsx)", "error");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      showMessage("Please select a file first", "error");
      return;
    }

    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
      setDownloadFilename("");
    }

    setIsUploading(true);
    setUploadProgress(0);
    setProgressMessage("");
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchWithAuth(
        `/intervention-points-targets/sync`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("Streaming response not supported by this browser.");
        }

        const decoder = new TextDecoder();
        let buffer = "";
        let finalResult = null;
        let streamError = null;

        const processEvent = (event) => {
          const status = event.status || "";

          if (status === "started") {
            setProgressMessage(
              event.message || "Preparing synchronization..."
            );
            setUploadProgress((prev) => (prev >= 10 ? prev : 10));
          } else if (status === "progress") {
            const percentage = Math.min(
              100,
              Math.max(0, Number(event.percentage) || 0)
            );
            setUploadProgress(percentage);
            setProgressMessage(event.message || "Processing rows...");
          } else if (status === "complete") {
            finalResult = event.result || null;
            setUploadProgress(100);
            setProgressMessage(event.message || "Synchronization complete");
          } else if (status === "error") {
            streamError =
              event.message || "Synchronization failed during processing.";
            setProgressMessage("");
          }
        };

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const event = JSON.parse(line);
              processEvent(event);
              if (event.status === "error") {
                break;
              }
            } catch (parseError) {
              console.error("Failed to parse stream chunk:", parseError);
            }
          }

          if (streamError) {
            break;
          }
        }

        if (buffer.trim() && !streamError) {
          try {
            const event = JSON.parse(buffer);
            processEvent(event);
          } catch (parseError) {
            console.error("Failed to parse final stream chunk:", parseError);
          }
        }

        if (streamError) {
          showMessage(streamError, "error");
        } else if (finalResult) {
          const { file: fileInfo, ...summary } = finalResult;
          setUploadResult(summary);

          if (fileInfo && fileInfo.content) {
            try {
              const byteCharacters = atob(fileInfo.content);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i += 1) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const blob = new Blob([new Uint8Array(byteNumbers)], {
                type:
                  fileInfo.mime_type ||
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              const url = window.URL.createObjectURL(blob);
              setDownloadUrl(url);
              setDownloadFilename(
                fileInfo.filename ||
                  "intervention_points_targets_sync_report.xlsx"
              );
            } catch (decodeError) {
              console.error("Failed to prepare download file:", decodeError);
            }
          }

          const visitTargetsCreated =
            summary.visit_targets_created ??
            summary.visits_targets_created ??
            0;
          const visitTargetsUpdated =
            summary.visit_targets_updated ??
            summary.visits_targets_updated ??
            0;
          const visitTargetsEnded =
            summary.visit_targets_ended ??
            summary.visits_targets_ended ??
            0;
          const qualificationsCreated =
            summary.qualifications_created ?? 0;
          const qualificationsUpdated =
            summary.qualifications_updated ?? 0;

          const defaultSummary = `Processed ${
            summary.total_processed ?? 0
          } rows: ${visitTargetsCreated} visit targets created, ${visitTargetsUpdated} updated${
            visitTargetsEnded ? `, ${visitTargetsEnded} ended` : ""
          }; ${qualificationsCreated} qualifications created, ${qualificationsUpdated} updated.`;

          const successMessage = summary.summary || defaultSummary;

          if ((summary.total_errors || 0) > 0) {
            showMessage(
              `${successMessage} ${summary.total_errors} errors encountered.`,
              "warning"
            );
          } else {
            showMessage(successMessage, "success");
          }

          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          showMessage(
            "Synchronization completed without a final response from the server.",
            "error"
          );
        }
      } else {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // ignore JSON parse failures for non-JSON errors
        }
        showMessage(
          (errorData && (errorData.message || errorData.detail)) ||
            "Upload failed",
          "error"
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      showMessage("Upload failed. Please try again.", "error");
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
        setProgressMessage("");
      }, 500);
    }
  };

  const downloadTemplate = async (includeData = false) => {
    if (includeData && !selectedClient) {
      showMessage(
        "Please select a client before downloading template with existing data.",
        "warning"
      );
      return;
    }

    setIsDownloading(true);
    try {
      const endpoint = includeData
        ? `/intervention-points-targets/template-with-data?client_id=${encodeURIComponent(
            selectedClient
          )}`
        : `/intervention-points-targets/template`;

      const response = await fetchWithAuth(endpoint);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = includeData
          ? "intervention_points_targets_with_existing.xlsx"
          : "intervention_points_targets_template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showMessage(
          includeData
            ? "Downloaded template with existing data"
            : "Downloaded empty template",
          "success"
        );
      } else {
        const errorData = await response.json();
        showMessage(errorData.detail || "Failed to download template", "error");
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      showMessage("Failed to download template", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadResults = () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        downloadFilename ||
        "intervention_points_targets_sync_report.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
      setDownloadFilename("");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
        <button
          onClick={() => window.history.back()}
          className="mr-4 mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          ← Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          Targets and Qualifications
        </h1>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Processing Logic
          </h2>
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              <strong>Visit Targets:</strong>
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4 mb-4">
              <li>
                If (intervention_point_id, client_id, start_date) exists:
                Updates frequency and/or duration
              </li>
              <li>
                If new: Creates new entry and ends existing entries with null
                end_date for the same (intervention_point_id, client_id)
              </li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              <strong>Intervention Points Qualification:</strong>
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
              <li>
                If (intervention_point_id, client_id, start_date) exists:
                Updates category, ranking, tiering, and/or animated
              </li>
              <li>If new: Creates new qualification entry</li>
            </ul>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Client
          </label>
          <ClientSelector
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            onError={showMessage}
            placeholder="Choose a client..."
          />
        </div>

        <div className="mb-6 space-y-3">
          <button
            onClick={() => downloadTemplate(false)}
            disabled={isDownloading}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-md transition-colors mr-3"
          >
            <Download className="w-5 h-5 mr-2" />
            {isDownloading ? "Downloading..." : "Download Empty Template"}
          </button>

          <button
            onClick={() => downloadTemplate(true)}
            disabled={isDownloading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-md transition-colors"
          >
            <FileText className="w-5 h-5 mr-2" />
            {isDownloading
              ? "Downloading..."
              : "Download Template with Existing Data"}
          </button>
        </div>

        <div className="mb-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="space-y-4">
              <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {file ? file.name : "Upload Excel file"}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {file
                    ? "File selected successfully"
                    : "Drag and drop an Excel file here, or click to browse"}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileInputChange}
                className="hidden"
              />
              {!file && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
                >
                  Browse Files
                </button>
              )}
            </div>
          </div>
        </div>

        {file && (
          <div className="mb-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {file.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200"
                : message.type === "warning"
                ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200"
                : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        {(isUploading || uploadProgress > 0) && (
          <div className="mb-6">
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Synchronization Progress
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {Math.min(uploadProgress, 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-2.5 bg-blue-600 transition-all duration-200"
                  style={{ width: `${Math.min(uploadProgress, 100)}%` }}
                />
              </div>
              {progressMessage && (
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  {progressMessage}
                </p>
              )}
            </div>
          </div>
        )}

        {uploadResult && (
          <div className="mb-6">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-600 border-b border-gray-200 dark:border-gray-500">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Synchronization Results
                </h3>
              </div>

              <div className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                  <div>
                    {uploadResult.summary && (
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {uploadResult.summary}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Processed {uploadResult.total_processed ?? 0} rows.{" "}
                      {uploadResult.total_errors ?? 0} errors captured.
                    </p>
                  </div>
                  {downloadUrl && (
                    <button
                      onClick={downloadResults}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Report
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {uploadResult.visit_targets_created ||
                        uploadResult.visits_targets_created ||
                        0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Visit Targets Created
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {uploadResult.visit_targets_updated ||
                        uploadResult.visits_targets_updated ||
                        0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Visit Targets Updated
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {uploadResult.qualifications_created || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Qualifications Created
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {uploadResult.qualifications_updated || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Qualifications Updated
                    </div>
                  </div>
                </div>

                {(() => {
                  const ended =
                    uploadResult.visit_targets_ended ??
                    uploadResult.visits_targets_ended ??
                    0;
                  if (!ended) {
                    return null;
                  }
                  return (
                    <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>{ended}</strong> previous visit targets were
                        ended due to new start dates
                      </p>
                    </div>
                  );
                })()}

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                      Error Details
                    </h4>
                    <div className="max-h-60 overflow-y-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <ul className="space-y-1">
                        {uploadResult.errors.map((error, index) => (
                          <li
                            key={index}
                            className="text-sm text-red-700 dark:text-red-300 font-mono"
                          >
                            {error}
                          </li>
                        ))}
                      </ul>
                      {uploadResult.total_errors >
                        uploadResult.errors.length && (
                        <div className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                          ... and{" "}
                          {uploadResult.total_errors -
                            uploadResult.errors.length}{" "}
                          more errors
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className={`inline-flex items-center px-6 py-3 font-medium rounded-md transition-colors ${
              !file || isUploading
                ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {isUploading ? (
              <>
                <Loader className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                Processing...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5 mr-2" />
                Synchronize Data
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TargetsAndQualifications;
